import { z } from "zod";

const hexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i);

export const featuredAccessCardBackgroundSchema = z.enum(["concert", "song"]);

export const featuredAccessCardLeadingVisualSchema = z.discriminatedUnion(
  "type",
  [
    z.object({
      type: z.literal("event-date"),
      month: z.string().min(1).max(3),
      day: z.string().min(1).max(2),
      year: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    }),
    z.object({
      type: z.literal("exclusive-artwork"),
      artworkUrl: z.string().url(),
    }),
  ],
);

export const featuredAccessCardShimmerSchema = z.object({
  /** Time taken for one left-to-right sweep. */
  durationMs: z.number().positive().optional(),
  /** Time from the start of one sweep to the start of the next. */
  intervalMs: z.number().positive().optional(),
});

export const featuredAccessCardCopySchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().min(1),
});

/** Compact right-hand CTA shared by all renderers. */
export const featuredAccessCardActionSchema = z.object({
  label: z.string().min(1).max(24),
  value: z.string().min(1).max(24).optional(),
  previousValue: z.string().min(1).max(24).optional(),
  /** Obscure the CTA copy and center a lock badge over it. */
  locked: z.boolean().optional(),
});

/** Data contract shared by every FeaturedAccessCard renderer. */
export const featuredAccessCardSchema = z.object({
  background: featuredAccessCardBackgroundSchema,
  accentColor: hexColorSchema,
  leadingVisual: featuredAccessCardLeadingVisualSchema.optional(),
  copy: featuredAccessCardCopySchema.optional(),
  action: featuredAccessCardActionSchema.optional(),
  locked: z.boolean().optional(),
  shimmer: featuredAccessCardShimmerSchema.optional(),
});

export type FeaturedAccessCardValue = z.infer<typeof featuredAccessCardSchema>;
export type FeaturedAccessCardBackground = z.infer<
  typeof featuredAccessCardBackgroundSchema
>;
export type FeaturedAccessCardLeadingVisual = z.infer<
  typeof featuredAccessCardLeadingVisualSchema
>;
export type FeaturedAccessCardShimmer = z.infer<
  typeof featuredAccessCardShimmerSchema
>;
export type FeaturedAccessCardCopy = z.infer<
  typeof featuredAccessCardCopySchema
>;
export type FeaturedAccessCardAction = z.infer<
  typeof featuredAccessCardActionSchema
>;
